import { AlertCircleIcon, CheckCircleIcon } from "lucide-react";

import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Spinner } from "@/components/ui/spinner";

import { ErrorBody } from "@/shared/components/Error";
import { ActionStatusPhase } from "@/shared/hooks/useActionStatusDialog";
import { AppError } from "@/shared/lib/utils/errors";

interface ActionStatusDialogProps {
  open: boolean;
  phase: ActionStatusPhase | null;
  onClose: () => void;
  onRetry?: () => void;
  error?: AppError | Error | null;

  successTitle: string;
  successDescription?: string;

  errorTitle: string;
}

/**
 * Shared alert dialog for action mutations (e.g. accept/decline invitation):
 * shows a centered spinner + "Loading..." while the mutation is in flight,
 * then either a success confirmation or an error with optional retry/close.
 *
 * The dialog can't be dismissed while `phase === "pending"`.
 */
export function ActionStatusDialog({ open, phase, onClose, onRetry, error, successTitle, successDescription, errorTitle }: ActionStatusDialogProps) {
  const status = error instanceof AppError ? error.status : undefined;

  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        {phase === "pending" && (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <Spinner className="size-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading...</p>
          </div>
        )}

        {phase === "success" && (
          <>
            <AlertDialogHeader>
              <div className="flex flex-col items-center gap-4 pb-2 text-center">
                <div className="flex size-14 items-center justify-center bg-emerald-500/10">
                  <CheckCircleIcon className="size-7 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <AlertDialogTitle>{successTitle}</AlertDialogTitle>
                  {successDescription && <AlertDialogDescription>{successDescription}</AlertDialogDescription>}
                </div>
              </div>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction onClick={onClose}>Done</AlertDialogAction>
            </AlertDialogFooter>
          </>
        )}

        {phase === "error" && error && (
          <>
            <AlertDialogHeader>
              <div className="flex flex-col items-center gap-4 pb-2 text-center">
                <div className="flex size-14 items-center justify-center bg-destructive/10">
                  <AlertCircleIcon className="size-7 text-destructive" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <AlertDialogTitle className="text-destructive">{errorTitle}</AlertDialogTitle>
                  <AlertDialogDescription>An error occurred. Review the details below and try again, or contact support if the problem persists.</AlertDialogDescription>
                </div>
              </div>
              <div className="relative border-l-2 border-destructive/40 bg-muted py-3 pl-4 pr-3 text-sm">
                {status !== undefined && <span className="absolute right-3 top-3 font-mono text-xs font-semibold text-destructive">HTTP {status}</span>}
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Error detail</p>
                <div className="text-sm text-foreground">
                  <ErrorBody error={error} />
                </div>
              </div>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={onClose}>Close</AlertDialogCancel>
              {onRetry && <AlertDialogAction onClick={onRetry}>Try again</AlertDialogAction>}
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
